import type ManualDerivativePlan from "./ManualDerivativePlan";

const emptyManualDerivativePlan: ManualDerivativePlan = {
  targetNodeId: null,
  targetNodeName: null,
  nodes: [],
};

/**
 * Finds the nodes the user is allowed to compute next.
 *
 * A node is ready once every node its chain rule sums over has been computed,
 * which is the same condition the automatic run satisfies by following the
 * topological order. Letting the user pick any ready node is what makes the
 * mode manual: several nodes are usually ready at once, and every choice
 * between them leads to the same numbers.
 *
 * @param plan The plan of the run.
 * @param computedNodeIds Node IDs already computed.
 * @returns Node IDs that can be computed now.
 */
const findReadyNodeIds = (
  plan: ManualDerivativePlan,
  computedNodeIds: ReadonlySet<string>,
): Set<string> => {
  const readyNodeIds = new Set<string>();
  plan.nodes.forEach((node) => {
    if (computedNodeIds.has(node.nodeId)) {
      return;
    }
    const isReady = node.prerequisiteNodeIds.every((prerequisiteNodeId) =>
      computedNodeIds.has(prerequisiteNodeId),
    );
    if (isReady) {
      readyNodeIds.add(node.nodeId);
    }
  });
  return readyNodeIds;
};

/**
 * Finds what a node is still waiting for.
 *
 * Naming the missing nodes is the whole feedback the user gets after picking
 * too early, so it has to be the actual list rather than a "not yet".
 *
 * @param plan The plan of the run.
 * @param nodeId Node ID of the node the user picked.
 * @param computedNodeIds Node IDs already computed.
 * @returns Node IDs blocking the pick, empty when nothing does.
 */
const findBlockingNodeIds = (
  plan: ManualDerivativePlan,
  nodeId: string,
  computedNodeIds: ReadonlySet<string>,
): string[] => {
  const node = plan.nodes.find((node) => node.nodeId === nodeId);
  if (node === undefined) {
    return [];
  }
  return node.prerequisiteNodeIds.filter(
    (prerequisiteNodeId) => !computedNodeIds.has(prerequisiteNodeId),
  );
};

export { emptyManualDerivativePlan, findBlockingNodeIds, findReadyNodeIds };
